/**
 * Dashboard auth — Entra OIDC via the FastAPI backend.
 *
 * On mount we call /api/auth/me. If the cookie is invalid we redirect to
 * /api/auth/login (which redirects to Entra and back). Sign-out clears the cookie.
 *
 * DEV_PREVIEW (no API base URL set + import.meta.env.DEV) bypasses the network
 * entirely so the UI is browsable without a backend running.
 */

import { createContext, useContext, useEffect, useState } from "react";

import { ApiError, auth as authApi } from "@/lib/api";
import type { AppSlug, Organisation, UserProfile } from "@/types/rbac";

const DEV_PREVIEW =
  import.meta.env.DEV && !import.meta.env.VITE_API_BASE_URL;

const DEV_PROFILE: UserProfile = {
  id: "dev-profile",
  user_id: "dev-user",
  org_id: "dev-org",
  role: "org_admin",
  site_ids: [],
  full_name: "Dev Preview",
  avatar_url: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

const DEV_ORG: Organisation = {
  id: "dev-org",
  name: "Demo Mining Co.",
  slug: "demo-mining",
  logo_url: null,
  created_at: new Date().toISOString(),
};

const DEV_APPS: AppSlug[] = ["operator", "dashboard", "earthworks"];

type DashboardUser = { id: string; email?: string };

interface AuthContextValue {
  user: DashboardUser | null;
  profile: UserProfile | null;
  org: Organisation | null;
  orgApps: AppSlug[];
  loading: boolean;
  signOut: () => Promise<void>;
  /** Send the browser to /api/auth/login to start the Entra OIDC flow. */
  signIn: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<DashboardUser | null>(DEV_PREVIEW ? { id: "dev-user", email: "dev@example.com" } : null);
  const [profile, setProfile] = useState<UserProfile | null>(DEV_PREVIEW ? DEV_PROFILE : null);
  const [org, setOrg] = useState<Organisation | null>(DEV_PREVIEW ? DEV_ORG : null);
  const [orgApps, setOrgApps] = useState<AppSlug[]>(DEV_PREVIEW ? DEV_APPS : []);
  const [loading, setLoading] = useState(!DEV_PREVIEW);

  useEffect(() => {
    if (DEV_PREVIEW) return;
    void hydrate();
  }, []);

  async function hydrate() {
    try {
      const me = await authApi.me();
      setUser({ id: me.user_id ?? "user", email: me.email });
      setProfile({
        id: me.user_id ?? "",
        user_id: me.user_id ?? "",
        org_id: me.org_id,
        role: me.role as UserProfile["role"],
        site_ids: [],
        full_name: me.full_name ?? null,
        avatar_url: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setOrg({
        id: me.org_id,
        name: "",
        slug: "",
        logo_url: null,
        created_at: new Date().toISOString(),
      });
      // org_apps query — could be /api/orgs/me. For now keep all apps enabled by default.
      setOrgApps(["operator", "dashboard"]);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setUser(null);
        setProfile(null);
        setOrg(null);
        setOrgApps([]);
      }
    } finally {
      setLoading(false);
    }
  }

  function signIn() {
    window.location.href = authApi.loginUrl();
  }

  async function signOut() {
    if (!DEV_PREVIEW) {
      try {
        await authApi.logout();
      } catch {
        // tolerated — local state below is the user-visible signal
      }
    }
    setUser(null);
    setProfile(null);
    setOrg(null);
    setOrgApps([]);
  }

  return (
    <AuthContext.Provider value={{ user, profile, org, orgApps, loading, signOut, signIn }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
}
